import { DataModel, SpanErrorLabel } from "../types/tree-view-types";
import { formatNumberByLocale } from "../lib/utils";

export const getExceptionType = (
  node: DataModel
): SpanErrorLabel | undefined => {
  const targetExceptionTypeArr = node.exceptionArrayType.find(
    (event) => event.attributes["exception.type"]
  );

  const exceptionType = targetExceptionTypeArr?.attributes["exception.type"];
  if (exceptionType) {
    return { kind: "exception", value: exceptionType };
  }

  if (node.statusMessage) {
    return { kind: "status", value: node.statusMessage };
  }

  if (node.statusDescription) {
    return { kind: "statusDescription", value: node.statusDescription };
  }

  return { kind: "status", value: "Unknown error" };
};

export const getTokens = (node: DataModel) => {
  const totalTokens = node.events.find((e) => {
    const inferenceNode =
      node.spanType?.toLowerCase() === "inference.framework" || "inference";

    return inferenceNode ? e.attributes.total_tokens : "";
  });

  const totalTokensString = totalTokens
    ? `${formatNumberByLocale(totalTokens.attributes.total_tokens)} tokens`
    : "";

  return totalTokensString;
};

// To calculate all tokens recursively
export const getAllTokensCount = (node: DataModel): string => {
  let total = 0;

  const dfs = (n: DataModel) => {
    // Add current node’s tokens
    const event = n.events.find((e) => {
      const inferenceNode =
        n.spanType?.toLowerCase() === "inference.framework" ||
        n.spanType?.toLowerCase() === "inference";
      return inferenceNode && e.attributes.total_tokens;
    });

    if (event) {
      total += Number(event.attributes.total_tokens || 0);
    }

    // Recurse into children
    if (n.children && n.children.length > 0) {
      n.children.forEach((child) => dfs(child));
    }
  };

  dfs(node);

  return total > 0 ? `${formatNumberByLocale(total)} tokens` : "";
};


export const expandParentsOfSpan = (
  spanId: string,
  data: DataModel[]
): Set<string> => {
  const expandedSet = new Set<string>();

  // DFS to find path from root to target span
  const findPath = (
    nodes: DataModel[],
    targetId: string,
    path: string[]
  ): string[] | null => {
    for (const node of nodes) {
      const newPath = [...path, node.id];

      if (node.id === targetId) {
        return newPath;
      }

      if (node.children && node.children.length > 0) {
        const result = findPath(node.children, targetId, newPath);
        if (result) return result;
      }
    }
    return null;
  };

  // Get full path from some root to the selected span
  const fullPath = findPath(data, spanId, []);

  if (!fullPath) {
    // spanId not found in tree
    return expandedSet;
  }

  // Parents = everything in the path except the last (the span itself)
  const parentIds = fullPath.slice(0, -1);

  parentIds.forEach((id) => expandedSet.add(id));

  return expandedSet;
};
